import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api';
import type {
    CreateRecordInput,
    CreateTestInput,
    PerformanceTest,
    PersonalRecord,
    SportDiscipline,
} from '../api';

// ─── Query Keys ───────────────────────────────────────────────────────────────
// Centralised so mutations can invalidate precisely.

export const queryKeys = {
    records: (sport?: SportDiscipline) =>
        sport ? (['personalRecords', sport] as const) : (['personalRecords'] as const),
    tests: (sport?: SportDiscipline) =>
        sport ? (['performanceTests', sport] as const) : (['performanceTests'] as const),
};

// ─── Personal Records ─────────────────────────────────────────────────────────

/** Fetch all records (optionally filtered by sport). */
// The generic is pinned explicitly: dependencies are installed without a lock
// file, and some @tanstack/react-query versions infer `data` as `unknown`,
// which makes every `data.filter((r) => ...)` call site an implicit-any error.
export function usePersonalRecords(sport?: SportDiscipline) {
    return useQuery<PersonalRecord[]>({
        queryKey: queryKeys.records(sport),
        queryFn: () => api.getMyRecords(sport),
    });
}

/** Create a personal record and refresh the list. */
export function useCreateRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateRecordInput) => api.createRecord(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.records() }),
    });
}

/** Update an existing record and refresh. */
export function useUpdateRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Partial<Omit<CreateRecordInput, 'sport'>> }) =>
            api.updateRecord(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.records() }),
    });
}

/** Soft-delete a record and refresh. */
export function useDeleteRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.deleteRecord(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.records() }),
    });
}

// ─── Performance Tests ────────────────────────────────────────────────────────

/** Fetch all tests (optionally filtered by sport). */
export function usePerformanceTests(sport?: SportDiscipline) {
    return useQuery<PerformanceTest[]>({
        queryKey: queryKeys.tests(sport),
        queryFn: () => api.getMyTests(sport),
    });
}

/** Create a performance test and refresh. */
export function useCreateTest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateTestInput) => api.createTest(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tests() }),
    });
}

/** Update a test result and refresh. */
export function useUpdateTest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Partial<Omit<CreateTestInput, 'sport'>> }) =>
            api.updateTest(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tests() }),
    });
}

/** Soft-delete a test and refresh. */
export function useDeleteTest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.deleteTest(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tests() }),
    });
}

// Re-export types so consumers don't need to reach into api/ directly
export type { PersonalRecord, PerformanceTest, SportDiscipline, CreateRecordInput, CreateTestInput } from '../api';
